import { Discount, hasDiscount } from "./discount"
import {
  FreshServiceProposal,
  SubmitFreshServiceProposal,
} from "./fresh-service-proposal"
import { buildDefaultSelection } from "./proposal-default-selection"
import { getProposalLevelPartIds } from "./proposal-level"
import { ProposalLevel } from "./proposal-level-kind"
import {
  getSelectedManualActionIds,
  getSelectedPartIds,
} from "./service-proposal-grouping"

/** Whether a selection buys a wash, on its own or through a package. */
const buysWash = (values: SubmitFreshServiceProposal): boolean =>
  values.selectedWashOriginalId !== undefined ||
  values.selectedPackages.some(
    (selectedPackage) => selectedPackage.selectedWashOriginalId !== undefined,
  )

/**
 * Whether the customer is buying everything the mechanic recommends:
 * Toppskick's work (or the whole proposal, when there are no levels) with
 * nothing taken out. Which packages carry the work doesn't matter, and
 * neither do "Tillval" products. A recommended wash must be bought too.
 *
 * Trafiksäker never is: it leaves out the work that can wait, and priced as
 * its own proposal (`getLevelProposal`) that work isn't even listed, so the
 * check below would find nothing missing.
 */
export const isFullApproval = (
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): boolean => {
  if (values.level === ProposalLevel.TRAFFIC_SAFE) return false
  if (values.isWashDeselected) return false
  // A package's wash can be taken out without touching `isWashDeselected`.
  // An unanswered choice between washes recommends none, so it still counts.
  if (buysWash(buildDefaultSelection(proposal)) && !buysWash(values)) {
    return false
  }

  const selectedPartIds = getSelectedPartIds(values)
  const hasEveryPart = [
    ...getProposalLevelPartIds(proposal, ProposalLevel.TOP_CONDITION),
  ].every((partId) => selectedPartIds.has(partId))
  if (!hasEveryPart) return false

  const selectedManualActionIds = getSelectedManualActionIds(values)
  return proposal.manualActions.every((action) =>
    selectedManualActionIds.has(action.id),
  )
}

/**
 * The order discount a selection gets: the full-approval discount while the
 * customer approves everything, otherwise the job's own order discount. The
 * CRM never sets both.
 */
export const getEffectiveOrderDiscount = (
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
  discounts: {
    orderDiscount: Discount | null
    fullApprovalOrderDiscount: Discount | null
  },
): Discount | null =>
  hasDiscount(discounts.fullApprovalOrderDiscount) &&
  isFullApproval(proposal, values)
    ? discounts.fullApprovalOrderDiscount
    : discounts.orderDiscount
